// src/oura/components/docs/DocDrawer.tsx
//
// One doc, read by Oura, in the side sheet (drawer specs v1.1, A-A). The
// board renders one <Drawer> and a DocSheet keyed per doc inside it, so the
// viewer walks the group the row sat in with previous and next (J and K).
//
//   Header   the file's icon, its title (cut at 2 lines), its code chips,
//            then "● {group} · {clock}": how long it has waited on us, its
//            reply-by or how late, when it was shared or edited.
//   Body     0 a notice when Oura's reads have stopped or failed, with "Read
//              the folder again";
//            1 "Fix first": every red or amber flag and its fix;
//            2 "Reply in the doc": the threads that need us, the client's
//              last words and a link to the comment;
//            3 "With the client": the questions still out, and the reply-by
//              with a "Change" button (ReplyByInline);
//            4 folds: Approved, File, Who can open it, Also in the doc.
//   Foot     Open in Google Docs (blue only where the next move is in the
//            doc) and Change the code, or Pick a code for a file with none.
//
// "Pick a code" is a view of this sheet (PickCodeView), never a drawer of its
// own. A save that moves the doc out of its group finishes it: "Filed under
// S4." and Next. Oura reads the doc; staff reply and resolve in the doc
// itself, so nothing here posts to it.
//
// Docs phase 2 (lane 3), behind the write switch: a "Questions and sharing"
// fold (write/DocWritePanel) opens write views over the doc, the way Pick a
// code does: the Questions editor, the name and Oura's 2 lines, Share with
// the client, and the review email and chaser (write/WriteSheet).
//
// Docs phase 2 (lane 4), behind the same switch: "Needs us" offers Resolve N
// agreed and opens each thread (threads/ThreadSheet: reply, resolve, reopen,
// log an answer, our own posts); the Approved fold becomes the approval
// record with the filed PDFs (threads/ApprovalFold), and an approved doc's
// foot files its PDF; the AI summary folds under the flags; "Also in the doc"
// offers Clear old placeholders.
//
// The board's row fills the sheet at once; the doc's threads load behind it.

import { useState, type ReactNode } from "react";
import { CalendarClockIcon, CheckCheckIcon, ChevronRightIcon, ExternalLinkIcon, HashIcon, HomeIcon, LockIcon, PencilLineIcon, RefreshCwIcon, ShieldAlertIcon, TriangleAlertIcon, UsersIcon } from "lucide-react";
import { rerunFill, resolveAgreed } from "@service/aeo/clientDocsWrite";
import {
    docsFailure,
    joinCodes,
    readDocsFolderNow,
    type BusinessDocuments,
    type CodeRow,
    type DocThread,
    type DocumentDetail,
    type StaffDocument,
} from "@service/aeo/clientDocs";
import { cn } from "../../lib/cn";
import { joinDotted } from "../../lib/oura";
import {
    DrawerBlock,
    DrawerFold,
    DrawerFoot,
    DrawerHead,
    DrawerNotice,
    DrawerPulse,
    DrawerQuote,
    FOCUS_RING,
    FamilyChip,
    IconSquare,
    LATE_TEXT,
    PillButton,
    PillLink,
    SURFACE,
    TYPE,
    Tag,
    type DrawerFootMessage,
} from "@oura/ui";
import {
    DUE_BY_NOTE,
    agoText,
    codeDueText,
    docCodeChips,
    docDate,
    docLateText,
    docsFailureText,
    fileStyle,
    fromAgoText,
    isCodeOverdue,
    isReadStale,
    ouraAccountOf,
    ouraAccountOnDoc,
    plural,
    trimText,
    useDocsConnection,
    useDocument,
} from "@oura/docs";
import { flowDone, flowMessage, useSheetVoice, type SheetFlow } from "../client/drawerFlow";
import { FLAG_FIX, NAME_TAG_TEXT, asSentence, decidedName, docSheetTitle, docStatusLine, fileNoun, groupOfDoc, noCodeText, pickTitle, useNow, type BoardGroupKey } from "./boardModel";
import { PickCodeView } from "./PickCodeDialog";
import { ReplyByInline } from "./ReplyByControl";
import { SkillRunBlock } from "./SkillRunBlock";
import { useSkillRun } from "../../docs/useSkillRun";
import { RenameToHouse } from "./create/RenameToHouse";
import { prefillFromWord } from "./create/createModel";
import { DocWritePanel } from "./write/DocWritePanel";
import { WriteSheet } from "./write/WriteSheet";
import { canShare, canWrite, isDocWriteView, type DocWriteView } from "./write/model";
import { ShareDonePanel } from "./write/ShareDonePanel";
import { ApprovalFold } from "./threads/ApprovalFold";
import { WordComments } from "./WordComments";
import { useFileApproved } from "./threads/fileApproved";
import { SummaryFold } from "./threads/SummaryFold";
import { usePlaceholderCleanUp } from "./threads/placeholderCleanUp";
import { agreedThreads, authorWord, clientAgreed, clientAgreedWithChange, filedForVersion, postWords, threadView, useApprovedReports, useDocSummaryOnOpen, withoutCode } from "./threads/model";

/**
 * The comment links ("Open the comment ↗") land on a comment through Drive's
 * `disco=` parameter. A11: test on one real doc before release; if the link
 * does not land on the comment, set this false and every link hides.
 */
const COMMENT_LINKS = true;

/** What each sharing role lets the client do. */
const ROLE_WORD: Record<"reader" | "commenter" | "writer" | "owner", string> = {
    reader: "can view",
    commenter: "can comment",
    writer: "can edit",
    owner: "owns it",
};


/** The doc's URL with `disco=` for one comment, joined with ? or &. */
function commentUrl(url: string, driveCommentId: string): string {
    return `${url}${url.includes("?") ? "&" : "?"}disco=${encodeURIComponent(driveCommentId)}`;
}

/* ------------------------------------------------------------------------- *
 * Body pieces
 * ------------------------------------------------------------------------- */

/** A line with a small coloured icon in front. */
function Line({ icon: Icon, tone = "text-gray-400", children }: { icon: typeof LockIcon; tone?: string; children: ReactNode }) {
    return (
        <li className="flex items-start gap-2">
            <Icon aria-hidden size={14} className={cn("mt-[3px] shrink-0", tone)} />
            <span className="min-w-0 break-words">{children}</span>
        </li>
    );
}

/** Text cut at `lines`; a click (or Enter) shows it whole. */
function ClampText({ text, lines, className }: { text: string; lines: 1 | 2; className?: string }) {
    const [all, setAll] = useState(false);
    return (
        <button
            type="button"
            aria-expanded={all}
            onClick={() => setAll(!all)}
            title={all ? undefined : text}
            className={cn(TYPE.tb, "block w-full min-w-0 rounded-md text-left text-gray-900", FOCUS_RING, className)}
        >
            {/* line-clamp sets its own display, so "block" only goes with the other two. */}
            <span className={cn(all ? "block whitespace-pre-line break-words" : lines === 1 ? "block truncate" : "line-clamp-2 whitespace-pre-line break-words")}>{text}</span>
        </button>
    );
}

/**
 * For a doc not shared with the client, when its codes are due on the plan:
 * "S4 is not sent yet. Was due Mon 14 Sep, the end of the Foundation build."
 * or, before the code's month starts, "G4 is due by Fri 13 Nov, the end of
 * plan month 2." The dates are the board's code rows; nothing here works one out.
 */
function dueLines(doc: StaffDocument, rows: readonly CodeRow[]): Array<{ key: string; text: string; late: boolean }> {
    if (doc.sharing.clientCanSee || doc.notClientDoc) return [];
    const lines = new Map<string, { labels: string[]; notSent: boolean; due: string; late: boolean }>();
    for (const chip of docCodeChips(doc)) {
        const row = rows.find((r) => r.code === chip.code && (r.outlet ?? null) === chip.outlet);
        if (!row || !row.expectedBy) continue;
        const notSent = row.state === "not_sent_yet";
        if (!notSent && row.state !== "not_shared") continue;
        const due = codeDueText(row, true);
        if (!due) continue;
        const key = `${notSent ? "owed" : "later"}|${due}`;
        const line = lines.get(key) ?? { labels: [], notSent, due, late: isCodeOverdue(row) };
        line.labels.push(chip.label);
        lines.set(key, line);
    }
    return Array.from(lines.entries()).map(([key, l]) => {
        const verb = l.labels.length === 1 ? "is" : "are";
        const text = l.notSent
            ? `${joinCodes(l.labels)} ${verb} not sent yet. ${l.due}.`
            : `${joinCodes(l.labels)} ${verb} ${l.due.charAt(0).toLowerCase()}${l.due.slice(1)}.`;
        return { key, text, late: l.late };
    });
}

function DueList({ lines }: { lines: ReturnType<typeof dueLines> }) {
    if (lines.length === 0) return null;
    return (
        <ul className={cn(TYPE.tc, "m-0 mt-2 list-plain space-y-1 p-0 text-gray-600")}>
            {lines.map((l) => (
                <Line key={l.key} icon={CalendarClockIcon} tone={l.late ? "text-rose-600" : "text-[#FF9500]"}>
                    <span title={DUE_BY_NOTE} className={cn(l.late && LATE_TEXT)}>
                        {l.text}
                    </span>
                </Line>
            ))}
        </ul>
    );
}

/** Every red or amber flag with its fix, in any group. The header already says "No code yet" and "Comments not read". */
function FixFirst({ doc, detail, group, action }: { doc: StaffDocument; detail: DocumentDetail | null; group: BoardGroupKey; action: ReactNode }) {
    const flags = doc.flags.filter((f) => f.code !== "no_code" && !(f.code === "comments_unreadable" && group === "not_read"));
    const internal = detail?.notCounted?.internalNotes ?? 0;
    // Internal notes the client can read, when the server sent no flag for them.
    const notesLine = internal > 0 && doc.sharing.clientCanSee && !flags.some((f) => f.code === "internal_notes");
    if (flags.length === 0 && !notesLine) return null;
    const ordered = [...flags.filter((f) => f.tone === "red"), ...flags.filter((f) => f.tone !== "red")];
    return (
        <DrawerBlock label="Fix first">
            <ul className="m-0 list-plain space-y-2 p-0 text-gray-700">
                {ordered.map((f) => {
                    const red = f.tone === "red";
                    const Icon = red ? ShieldAlertIcon : TriangleAlertIcon;
                    // A Word file already converted: the server's sentence names its Google Doc, nothing to fix (#4307).
                    const fix = f.code === "word_file" && doc.convertedTo ? undefined : FLAG_FIX[f.code];
                    return (
                        <li key={f.code} className="flex items-start gap-2">
                            <Icon aria-hidden size={14} className={cn("mt-[3px] shrink-0", red ? "text-rose-600" : "text-amber-600")} />
                            <span className="min-w-0 break-words">
                                <span className={red ? "font-medium text-rose-700" : "font-medium text-amber-800"}>{fix ? asSentence(f.message) : f.message}</span>
                                {fix && <span className="text-gray-700"> {fix}</span>}
                            </span>
                        </li>
                    );
                })}
                {notesLine && (
                    <li className="flex items-start gap-2">
                        <ShieldAlertIcon aria-hidden size={14} className="mt-[3px] shrink-0 text-rose-600" />
                        <span className="min-w-0 break-words">
                            <span className="font-medium text-rose-700">{plural(internal, "internal note")} the client can read.</span>
                            <span className="text-gray-700"> {FLAG_FIX.internal_notes}</span>
                        </span>
                    </li>
                )}
            </ul>
            <div className="mt-3">{action}</div>
        </DrawerBlock>
    );
}

/** One thread that needs us: its number or "Raised by the client", the question, the client's words, the comment link. */
function ReplyThread({ t, n, doc, readsComments, onOpen }: { t: DocThread; n: number | null; doc: StaffDocument; readsComments: boolean; onOpen?: () => void }) {
    const [earlier, setEarlier] = useState(false);
    const raised = t.kind === "raised_by_client";
    const said = t.posts.filter((p) => p.text.trim() && p.action !== "resolve");
    // The client's last words; for a thread they raised with no reply since, the opening itself.
    const last = [...said].reverse().find((p) => p.author.side === "client") ?? null;
    const rest = said.filter((p) => p !== last);
    const chips = docCodeChips(doc);
    const ownCode = chips.length === 1 && chips[0].label === t.code;
    const link = COMMENT_LINKS && readsComments && t.driveCommentId ? commentUrl(doc.url, t.driveCommentId) : null;
    const label = n !== null ? `question ${n}` : "the client's comment";
    return (
        <div className={cn("border-t py-3 first:border-t-0 first:pt-0 last:pb-0", SURFACE.hairline)}>
            <div className="flex flex-wrap items-center gap-1.5">
                {raised ? <Tag tone="blue">Raised by the client</Tag> : <span className={cn(TYPE.tc, "font-semibold text-gray-500")}>Q{n}</span>}
                {t.code && !ownCode && <FamilyChip family={chips.find((c) => c.label === t.code)?.family ?? "x"}>{t.code}</FamilyChip>}
                {onOpen && clientAgreed(t) && <Tag tone="green">Agreed</Tag>}
                {onOpen && clientAgreedWithChange(t) && <Tag tone="amber">Agreed with a change</Tag>}
            </div>
            {/* The text they commented on reads before their words about it. */}
            {raised && t.quoted && <p className={cn(TYPE.tc, "m-0 mt-2 break-words text-gray-500")}>On &ldquo;{t.quoted}&rdquo;</p>}
            {raised ? (
                <div className={t.quoted ? "mt-1" : "mt-2"}>
                    <DrawerQuote who={authorWord(t.openedBy)} where="in the doc" at={t.openedAt}>
                        {t.text}
                    </DrawerQuote>
                </div>
            ) : (
                <div className="mt-1">
                    <ClampText text={withoutCode(t.text, t.kind)} lines={2} />
                </div>
            )}
            {!raised && t.quoted && <p className={cn(TYPE.tc, "m-0 mt-1 break-words text-gray-500")}>On &ldquo;{t.quoted}&rdquo;</p>}
            {earlier &&
                rest.map((p) => (
                    <div key={p.id} className="mt-2">
                        <DrawerQuote {...postWords(p)}>{p.text}</DrawerQuote>
                    </div>
                ))}
            {last && (
                <div className="mt-2">
                    <DrawerQuote {...postWords(last)}>{last.text}</DrawerQuote>
                </div>
            )}
            {(rest.length > 0 || link || onOpen) && (
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
                    {/* Phase 2: reply and resolve from Oura, in the thread's own view. */}
                    {onOpen && (
                        <PillButton variant="text" onClick={onOpen} aria-label={`Reply or resolve ${label}`}>
                            Reply or resolve
                        </PillButton>
                    )}
                    {rest.length > 0 && (
                        <PillButton variant="text" aria-expanded={earlier} onClick={() => setEarlier(!earlier)}>
                            {earlier ? "Hide earlier replies" : `${rest.length} earlier ${rest.length === 1 ? "reply" : "replies"}`}
                        </PillButton>
                    )}
                    {link && (
                        <a
                            href={link}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={`Open ${label} in Google Docs, new tab`}
                            className={cn(
                                TYPE.tc,
                                "relative inline-flex items-center gap-1 rounded font-medium text-blue-600 hover:text-blue-700 after:absolute after:-inset-x-1 after:-inset-y-[13px] after:content-['']",
                                FOCUS_RING,
                            )}
                        >
                            Open the comment
                            <ExternalLinkIcon aria-hidden size={12} className="shrink-0" />
                        </a>
                    )}
                </div>
            )}
        </div>
    );
}

/** The fold's content: what is in the doc and never counts. */
function AlsoList({ detail }: { detail: DocumentDetail }) {
    const c = detail.notCounted;
    if (!c) return null;
    const internal = detail.threads.filter((t) => t.internal);
    return (
        <>
            <ul className="m-0 list-plain space-y-1.5 p-0 text-gray-700">
                {c.internalNotes > 0 && (
                    <li>
                        <span className="font-semibold text-gray-900">{c.internalNotes}</span> internal {c.internalNotes === 1 ? "note" : "notes"} from our own accounts
                        {internal.length > 0 && (
                            <ul className={cn(TYPE.tc, "m-0 mt-1 list-plain space-y-1 p-0 text-gray-500")}>
                                {internal.map((t) => (
                                    <li key={t.id} className="break-words">
                                        {joinDotted(authorWord(t.openedBy), docDate(t.openedAt))}: &ldquo;{t.text}&rdquo;
                                    </li>
                                ))}
                            </ul>
                        )}
                    </li>
                )}
                {c.placeholders > 0 && (
                    <li>
                        <span className="font-semibold text-gray-900">{c.placeholders}</span> {c.placeholders === 1 ? "placeholder" : "placeholders"} asking nothing
                    </li>
                )}
                {c.ourNotes > 0 && (
                    <li>
                        <span className="font-semibold text-gray-900">{c.ourNotes}</span> {c.ourNotes === 1 ? "note" : "notes"} from us asking nothing
                    </li>
                )}
            </ul>
            <p className={cn(TYPE.tc, "m-0 mt-2 text-gray-500")}>Oura never counts these.</p>
        </>
    );
}

/** Where the codes came from, in a few words. */
function codesFrom(doc: StaffDocument): { text: string; tone: keyof typeof NAME_TAG_TEXT } {
    const n = doc.codes.length;
    if (doc.notClientDoc) return { text: "Marked internal. Not a client doc.", tone: "gray" };
    if (n === 0) return { text: noCodeText(doc), tone: "gray" };
    if (doc.codeSource === "staff") return { text: n === 1 ? "Code picked by hand" : "Codes picked by hand", tone: "blue" };
    if (doc.codeSource === "title") return { text: n === 1 ? "Code from the page title" : "Codes from the page title", tone: "blue" };
    return { text: n === 1 ? "Code from the file name" : "Codes from the file name", tone: "gray" };
}

/* ------------------------------------------------------------------------- *
 * The sheet
 * ------------------------------------------------------------------------- */

export function DocSheet({
    docId,
    boardDoc,
    board,
    businessId,
    businessName,
    mode,
    onPick,
    onPickCancel,
    onPickSaved,
    onView,
    flow,
}: {
    docId: string;
    /** The board's row for this doc, so the sheet fills before its threads load. Null once it has left the folder. */
    boardDoc: StaffDocument | null;
    /** The board: its code rows (when codes are due), when Oura last read the folder, and whether that read failed. */
    board: BusinessDocuments | null;
    businessId: string;
    businessName: string;
    /** The doc, or Pick a code over it. */
    mode: "doc" | "pick" | DocWriteView;
    /** The foot's Pick a code or Change the code. */
    onPick: () => void;
    /** Cancel in Pick a code: back to the doc, or close when it opened from a row. */
    onPickCancel: () => void;
    /** A pick saved: back to the doc. */
    onPickSaved: () => void;
    /** A write view over the doc (phase 2), or "doc" to come back. Absent: no write views here. */
    onView?: (view: DocWriteView | "doc") => void;
    flow: SheetFlow;
}) {
    const load = useDocument(docId);
    const now = useNow();
    // WHO OURA READS AS is a property of the connection, not of this
    // file's permission list. Same cached read the folder card uses.
    const connection = useDocsConnection();
    const detail = load.data;
    // The detail is newer once it has arrived; the board's row until then.
    const doc: StaffDocument | null = detail ?? boardDoc;
    const [said, setSaid] = useState<string | null>(null);
    const [message, setMessage] = useState<DrawerFootMessage | null>(null);
    const [reading, setReading] = useState(false);
    const voice = useSheetVoice(setSaid, setMessage);

    const busy = (b: boolean) => flow.onBusy?.(b);
    const rows = board?.codes ?? [];

    // Phase 2 (lane 4): only a doc the write switch and its file type allow.
    const writable = Boolean(onView && doc && canWrite(doc));
    const [agreeing, setAgreeing] = useState(false);
    const [rerunning, setRerunning] = useState(false);
    const reports = useApprovedReports(writable ? businessId : null);
    // The run that wrote this doc, when a skill did (#3983): found in the Skill library's slot for
    // the doc type, and read again until it is no longer running. Before the early returns below.
    // The doc's own run from the server (#6161) is held until the library lists it, so one
    // outside the library's last 5 still shows.
    const skillRun = useSkillRun(docId, doc?.docType ?? null, { enabled: writable, initial: doc?.skillRun ?? null });
    // Held here, above the write views, so it runs once per opening: back from a reply it is not made again.
    const summary = useDocSummaryOnOpen(writable && doc && fileStyle(doc.fileType).readsComments ? doc.id : null, detail);
    const footNote = (m: DrawerFootMessage) => {
        setSaid(null);
        voice.note(m);
    };
    const fileAction = useFileApproved({
        doc,
        businessName,
        reports,
        enabled: writable && mode === "doc",
        onBusy: busy,
        onNote: footNote,
        // Said, so the foot offers the next doc, as after a pick.
        onFiled: (text) => {
            setMessage(null);
            voice.said(text);
        },
    });
    const cleanUp = usePlaceholderCleanUp({
        businessId,
        businessName,
        enabled: writable && mode === "doc" && (detail?.notCounted?.placeholders ?? 0) > 0,
        onBusy: busy,
        onNote: footNote,
    });

    const readAgain = async () => {
        if (reading) return;
        setReading(true);
        busy(true);
        const r = await readDocsFolderNow(businessId);
        setReading(false);
        busy(false);
        setSaid(null);
        voice.note(r.ok ? { tone: "info", text: "Oura is reading the folder again." } : { tone: "error", text: docsFailureText(r, "the folder") });
    };
    const readAgainButton = (
        <PillButton variant="text" busy={reading} onClick={() => void readAgain()}>
            Read the folder again
        </PillButton>
    );

    // Gone: the board no longer lists it and it cannot be read.
    if (!doc) {
        if (load.status === "loading") {
            return (
                <>
                    <DrawerHead title="Document" />
                    <DrawerBlock>
                        <DrawerPulse lines={3} label="Loading the document" />
                    </DrawerBlock>
                </>
            );
        }
        return (
            <>
                <DrawerHead title="Document" />
                <DrawerBlock>
                    <p className="m-0 text-gray-700">This file is no longer in the client&rsquo;s folder.</p>
                </DrawerBlock>
            </>
        );
    }

    const group = groupOfDoc(doc);
    const file = fileStyle(doc.fileType);
    const title = docSheetTitle(doc);
    const chips = docCodeChips(doc);
    const status = docStatusLine(doc, group, rows);
    const lead = <IconSquare tone={file.tone} icon={file.icon} />;
    const chipList = (chips.length > 0 || doc.custom) && (
        <>
            {doc.custom && <Tag tone="gray">Custom</Tag>}
            {chips.map((c) => (
                <FamilyChip key={c.label} family={c.family}>
                    {c.label}
                </FamilyChip>
            ))}
        </>
    );
    // A file whose name carries its codes keeps them: they are changed by renaming it.
    const canPick = doc.codeSource !== "name";
    const pickLabel = doc.codes.length === 0 && !doc.notClientDoc ? "Pick a code" : "Change the code";

    if (mode === "pick" && board) {
        return (
            <>
                <DrawerHead lead={lead} leadKind="square" title={pickTitle(doc)} status={status} />
                <PickCodeView
                    doc={doc}
                    board={board}
                    onBusy={busy}
                    onCancel={onPickCancel}
                    onSaved={(text) => {
                        setMessage(null);
                        voice.said(text);
                        onPickSaved();
                    }}
                />
            </>
        );
    }

    if (isDocWriteView(mode) && onView) {
        return (
            <WriteSheet
                view={mode}
                doc={doc}
                detail={detail}
                loading={load.status === "loading"}
                businessId={businessId}
                businessName={businessName}
                now={now}
                plan={board?.plan ?? null}
                lead={lead}
                status={status}
                onBusy={busy}
                onView={onView}
            />
        );
    }

    const done = flowDone(flow, said);
    const footMessage = flowMessage(done, message, said);

    /**
     * The fill of a doc made from a template (#4185). A failed or stalled fill left
     * the doc in Drive as the failure notice; run it again here, so nobody has to
     * delete it in Drive and create it a second time. Only the server says when
     * that is allowed (`canRerun`): never worked out from a status or an age.
     */
    const fill = detail?.fill ?? doc.fill ?? null;
    const rerunTheFill = async () => {
        if (rerunning) return;
        setRerunning(true);
        busy(true);
        // Busy always clears: a call that throws reads as a failed one.
        let r: Awaited<ReturnType<typeof rerunFill>>;
        try {
            r = await rerunFill(doc.id);
        } catch {
            r = docsFailure("network");
        } finally {
            setRerunning(false);
            busy(false);
        }
        if (!r.ok) return footNote({ tone: "error", text: docsFailureText(r, "the doc") });
        setMessage(null);
        voice.said("Oura is filling this doc again. It takes up to two minutes");
    };

    /* The notice: a failed fill, a failed refresh of this doc, a folder Oura cannot read, or reads that stopped. */
    let notice: ReactNode = null;
    if (fill && fill.canRerun) {
        notice = (
            <DrawerNotice
                action={writable ? <PillButton variant="text" busy={rerunning} onClick={() => void rerunTheFill()}>Re-run the fill</PillButton> : undefined}
            >
                {fill.error ?? "Oura started filling this doc and did not finish it."}
            </DrawerNotice>
        );
    } else if (fill && fill.status === "running") {
        notice = <DrawerNotice>Oura is filling this doc now. It takes up to two minutes.</DrawerNotice>;
    } else if (load.status === "ready" && load.error) {
        const from = fromAgoText(load.loadedAt, now);
        notice = (
            <DrawerNotice action={<PillButton variant="text" onClick={load.reload}>Try again</PillButton>}>
                {docsFailureText(load.error, "this document")} {from ? `This is the read from ${from}.` : null}
            </DrawerNotice>
        );
    } else if (!detail && load.status === "error" && load.error) {
        notice = (
            <DrawerNotice action={<PillButton variant="text" onClick={load.reload}>Try again</PillButton>}>
                {docsFailureText(load.error, "the questions")}
            </DrawerNotice>
        );
    } else if (board?.folder?.lastError) {
        notice = <DrawerNotice action={readAgainButton}>Oura could not read the folder. {board.folder.lastError.message}</DrawerNotice>;
    } else if (board && isReadStale(board.readAt, 5, now)) {
        notice = <DrawerNotice action={readAgainButton}>Oura last read the folder {agoText(board.readAt, now)}. Reads have stopped.</DrawerNotice>;
    }

    /* The threads, once read. */
    const threads = detail?.threads ?? [];
    const questions = threads.filter((t) => t.kind === "question");
    const qNumber = (t: DocThread) => (t.kind === "question" ? questions.indexOf(t) + 1 : null);
    const needs = [
        ...threads.filter((t) => t.kind === "raised_by_client" && t.state === "needs_us"),
        ...questions.filter((t) => t.state === "needs_us"),
    ];
    const shared = doc.sharing.clientCanSee;
    const out = questions.filter((t) => t.state === "waiting_on_client" || t.state === "late");
    // In a doc the client cannot see, every open question is still in the doc.
    const inDoc = shared ? [] : questions.filter((t) => !t.resolved);
    const resolved = threads.filter((t) => t.resolved && (t.kind === "question" || t.kind === "raised_by_client"));
    // A comment the client raised that we answered and nobody has closed: kept in view, with Resolve, until it is.
    const replied = threads.filter((t) => t.kind === "raised_by_client" && !t.resolved && t.state !== "needs_us");
    const due = dueLines(doc, rows);
    const openThread = writable && onView ? (id: string) => onView(threadView(id)) : null;
    const agreed = writable ? agreedThreads(threads) : [];

    /** Resolve N agreed: one click for every open question the client agreed to, exactly the ones shown. */
    const resolveAllAgreed = async () => {
        if (agreeing || agreed.length === 0) return;
        const ids = agreed.map((t) => t.id);
        setAgreeing(true);
        busy(true);
        // Busy always clears: a call that throws reads as a failed one.
        let r: Awaited<ReturnType<typeof resolveAgreed>>;
        try {
            r = await resolveAgreed(doc.id, { threadIds: ids });
        } catch {
            r = docsFailure("network");
        } finally {
            setAgreeing(false);
            busy(false);
        }
        if (!r.ok) return footNote({ tone: "error", text: docsFailureText(r, "the doc") });
        setMessage(null);
        voice.said(`Resolved ${plural(ids.length, "question")} the client agreed to`);
    };

    // The reply-by in the "With the client" head: the header says it already in Waiting on client.
    const replyBy = docDate(doc.replyBy);
    const lateNote = doc.late ? docLateText(doc) : null;
    const headClock = group !== "waiting_on_client" && shared && out.length > 0 && replyBy;
    const canChangeReplyBy = shared && doc.state !== "approved" && out.length > 0;

    /* The folds. */
    const decided = file.readsComments ? decidedName(doc, businessName, now, board?.plan ?? null) : null;
    const tag = codesFrom(doc);
    const clients = doc.sharing.sharedWith.filter((p) => !p.ours && p.role !== "owner");
    // The account Oura acts as, from the connection -- NOT the first
    // ourteam address that happens to be on the doc. A client folder is
    // shared with the team, so `sharedWith` carries several of ours and
    // the alphabetically-first one was winning: this read "Oura reads it
    // as jack@ourteam.ai" on a doc Oura reads as no-reply@ourteam.ai,
    // naming a colleague with view access as the integration.
    //
    // The doc's own owner is the fallback ONLY BEFORE THE CONNECTION HAS
    // ANSWERED. Once it has, its answer stands -- including "no account":
    // disconnected or unconfigured is a real reply, and falling through to the
    // owner there would name an account the server declined to name (Jack,
    // #4061). `data` is null until the first good read, so that is the test.
    //
    // The fallback is also only ever ours: a linked doc can be owned by
    // someone outside ourteam, and that is never who Oura reads as.
    const ouraAccount = connection.data ? ouraAccountOf(connection.data) : ouraAccountOnDoc(doc);
    const sharedDay = docDate(doc.sharing.sharedAt);
    const edited = docDate(doc.modifiedAt);
    const firstClient = clients[0];
    const shareNote = firstClient
        ? `${firstClient.name ?? firstClient.email ?? "Someone"} ${ROLE_WORD[firstClient.role]}${clients.length > 1 ? ` · ${clients.length - 1} more` : ""}`
        : "Not shared with the client";
    const also = detail?.notCounted;
    const alsoCount = also ? also.internalNotes + also.placeholders + also.ourNotes : 0;

    /* The foot. */
    const openLink = (primary: boolean) => (
        <PillLink variant={primary ? "primary" : "secondary"} href={doc.url} target="_blank" rel="noopener noreferrer" icon={ExternalLinkIcon}>
            {file.openLabel}
            <span className="sr-only">, opens in a new tab</span>
        </PillLink>
    );
    const pickButton = (primary: boolean) => (
        <PillButton variant={primary ? "primary" : "secondary"} icon={HashIcon} disabled={reading} onClick={onPick}>
            {pickLabel}
        </PillButton>
    );
    // Phase 2: an approved doc's next move is a named person filing its PDF.
    const fileButton = fileAction.button;
    const first = fileButton ?? (group === "no_code" ? pickButton(true) : openLink(group === "needs_us" || group === "not_shared"));
    const second = fileButton ? openLink(false) : group === "no_code" ? openLink(false) : canPick ? pickButton(false) : null;
    const confirm = fileAction.confirm ?? cleanUp.confirm ?? summary.confirm ?? null;
    // A Word file: Convert opens Create on it with the file picked; one already converted opens its Google Doc (#4307).
    const converted = doc.fileType === "word" ? doc.convertedTo ?? null : null;
    const convertButton =
        doc.fileType !== "word" ? null : converted ? (
            flow.onOpenDoc ? (
                <PillButton variant="text" onClick={() => flow.onOpenDoc?.(converted.id)}>
                    Open the Google Doc
                </PillButton>
            ) : null
        ) : flow.onCreate ? (
            <PillButton variant="text" onClick={() => flow.onCreate?.(prefillFromWord(doc))}>
                Convert to a Google Doc
            </PillButton>
        ) : null;

    return (
        <>
            <DrawerHead lead={lead} leadKind="square" title={title} status={status} chips={chipList || undefined} clampTitle />

            {notice}
            {fill && fill.status === "done" && fill.canRefill && writable && (
                // A filled doc filled again in place (#4874): no second doc, no version.
                <DrawerBlock label="Template fill">
                    <p className={cn(TYPE.tc, "m-0 text-gray-600")}>
                        Filled from the template. Fill it again to start over with the client&rsquo;s latest data; this fill
                        is kept in the folder as &ldquo;Earlier fill&rdquo;.
                    </p>
                    <PillButton variant="text" className="mt-2" icon={RefreshCwIcon} busy={rerunning} onClick={() => void rerunTheFill()}>
                        Fill it again
                    </PillButton>
                </DrawerBlock>
            )}
            {skillRun && (
                <SkillRunBlock
                    // Keyed by the doc: its open form and typed note belong to this doc, and
                    // moving to another must not carry them over to a paid run against it.
                    key={doc.id}
                    run={skillRun}
                    docId={doc.id}
                    canRerun={writable}
                    now={now}
                    onBusy={busy}
                    onNote={footNote}
                    onOpenDoc={flow.onOpenDoc}
                />
            )}
            <FixFirst
                doc={doc}
                detail={detail}
                group={group}
                action={
                    <div className="flex flex-wrap gap-2">
                        {convertButton}
                        {readAgainButton}
                    </div>
                }
            />
            {writable && file.readsComments && <SummaryFold state={summary} detail={detail} filed={Boolean(filedForVersion(reports.data, doc))} flags={doc.flags} />}

            {/* A Word file's own comments, read from the .docx (#4565). They are not Drive
                comments, so the threads below are empty for it; Word files count as
                readsComments, so this cannot sit in the branch for files Oura doesn't read. */}
            {detail?.wordFile?.comments?.length ? <WordComments comments={detail.wordFile.comments} /> : null}

            {!file.readsComments ? (
                <DrawerBlock>
                    <p className="m-0 text-gray-700">Oura doesn&rsquo;t read comments in {fileNoun(doc.fileType)}.</p>
                </DrawerBlock>
            ) : !detail ? (
                load.status === "loading" && (
                    <DrawerBlock>
                        <DrawerPulse lines={3} label="Loading the questions" />
                    </DrawerBlock>
                )
            ) : (
                <>
                    {needs.length > 0 && (
                        <DrawerBlock
                            label={
                                openThread ? (
                                    <span className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                                        <span>Needs us · {needs.length}</span>
                                        {agreed.length > 0 && (
                                            <PillButton size="sm" icon={CheckCheckIcon} busy={agreeing} onClick={() => void resolveAllAgreed()}>
                                                Resolve {agreed.length} agreed
                                            </PillButton>
                                        )}
                                    </span>
                                ) : (
                                    `Reply in the doc · ${needs.length}`
                                )
                            }
                        >
                            <div className="mt-2">
                                {needs.map((t) => (
                                    <ReplyThread
                                        key={t.id}
                                        t={t}
                                        n={t.number ?? qNumber(t)}
                                        doc={doc}
                                        readsComments={file.readsComments}
                                        onOpen={openThread ? () => openThread(t.id) : undefined}
                                    />
                                ))}
                            </div>
                        </DrawerBlock>
                    )}
                    {out.length > 0 && shared && (
                        <div className={SURFACE.block}>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                <p className={cn(TYPE.lab, "m-0")}>With the client · {out.length}</p>
                                {headClock && (
                                    <span className={cn(TYPE.tc, "tabular-nums text-gray-600")}>
                                        Reply by {replyBy}
                                        {lateNote && (
                                            <>
                                                {" · "}
                                                <span className={LATE_TEXT}>{lateNote}</span>
                                            </>
                                        )}
                                    </span>
                                )}
                                {/* Closed, "Change" sits at the right; open, the date field takes its own line. */}
                                {canChangeReplyBy && (
                                    <ReplyByInline
                                        doc={doc}
                                        onBusy={busy}
                                        onSaved={(text) => {
                                            setSaid(null);
                                            voice.note({ tone: "success", text });
                                        }}
                                    />
                                )}
                            </div>
                            <ul className="m-0 mt-2 list-plain p-0">
                                {out.map((t) => (
                                    <li key={t.id} className={cn("flex items-start gap-2 border-t py-1.5 first:border-t-0", SURFACE.hairline)}>
                                        <span className={cn(TYPE.tc, "w-6 shrink-0 pt-0.5 font-semibold text-gray-500")}>Q{t.number ?? qNumber(t)}</span>
                                        <span className="min-w-0 flex-1">
                                            {/* Phase 2: the question opens its thread, where an answer by email, WhatsApp or a call is logged. */}
                                            {openThread ? (
                                                <button
                                                    type="button"
                                                    onClick={() => openThread(t.id)}
                                                    className={cn(TYPE.tb, "flex w-full min-w-0 items-center gap-2 rounded-md text-left text-gray-900", FOCUS_RING)}
                                                >
                                                    <span className="min-w-0 flex-1 truncate">{withoutCode(t.text, t.kind)}</span>
                                                    <ChevronRightIcon aria-hidden size={14} className="shrink-0 text-gray-400" />
                                                </button>
                                            ) : (
                                                <ClampText text={withoutCode(t.text, t.kind)} lines={1} />
                                            )}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {replied.length > 0 && (
                        <DrawerBlock label={`We replied · ${replied.length}`}>
                            <p className={cn(TYPE.tc, "m-0 text-gray-500")}>Comments the client raised. Resolve each once it is settled.</p>
                            <ul className="m-0 mt-2 list-plain p-0">
                                {replied.map((t) => (
                                    <li key={t.id} className={cn("flex items-center gap-2 border-t py-1.5 first:border-t-0", SURFACE.hairline)}>
                                        <span className="min-w-0 flex-1">
                                            <ClampText text={withoutCode(t.text, t.kind)} lines={1} />
                                        </span>
                                        {openThread && (
                                            <PillButton size="sm" onClick={() => openThread(t.id)}>
                                                Resolve<span className="sr-only">: {trimText(withoutCode(t.text, t.kind), 40)}</span>
                                            </PillButton>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </DrawerBlock>
                    )}
                    {inDoc.length > 0 && (
                        <DrawerBlock label={`In the doc · ${inDoc.length}`}>
                            <p className={cn(TYPE.tc, "m-0 text-gray-500")}>The client can&rsquo;t see these yet.</p>
                            <ul className="m-0 mt-2 list-plain p-0">
                                {inDoc.map((t) => (
                                    <li key={t.id} className={cn("flex items-start gap-2 border-t py-1.5 first:border-t-0", SURFACE.hairline)}>
                                        <span className={cn(TYPE.tc, "w-6 shrink-0 pt-0.5 font-semibold text-gray-500")}>Q{qNumber(t)}</span>
                                        <span className="min-w-0 flex-1">
                                            <ClampText text={withoutCode(t.text, t.kind)} lines={1} />
                                        </span>
                                    </li>
                                ))}
                            </ul>
                            <DueList lines={due} />
                        </DrawerBlock>
                    )}
                    {openThread ? (
                        <ApprovalFold doc={doc} detail={detail} businessId={businessId} reports={reports} onOpenThread={openThread} />
                    ) : resolved.length > 0 && (
                        <DrawerFold id="staff-doc.approved" label={`Approved · ${resolved.length}`} remember>
                            <ul className="m-0 list-plain p-0">
                                {resolved.map((t) => {
                                    const resolvedIn = [...t.posts].reverse().find((p) => p.action === "resolve")?.where;
                                    return (
                                        <li key={t.id} className={cn("border-t py-2 first:border-t-0 first:pt-0", SURFACE.hairline)}>
                                            <ClampText text={withoutCode(t.text, t.kind)} lines={2} />
                                            <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-500")}>
                                                {joinDotted(
                                                    t.resolvedBy ? `Resolved by ${authorWord(t.resolvedBy)} ${resolvedIn === "oura" ? "in Oura" : "in the doc"}` : "Resolved",
                                                    docDate(t.resolvedAt),
                                                )}
                                            </p>
                                        </li>
                                    );
                                })}
                            </ul>
                        </DrawerFold>
                    )}
                </>
            )}

            {onView && canWrite(doc) && <DocWritePanel doc={doc} detail={detail} businessId={businessId} businessName={businessName} now={now} plan={board?.plan ?? null} onView={onView} />}
            {/* A client PDF: share it and mark it Done from here too, as a Google Doc can (3 Nov 2026). */}
            {onView && !canWrite(doc) && canShare(doc) && <ShareDonePanel doc={doc} onView={onView} />}

            <DrawerFold id="staff-doc.file" label="File" note={joinDotted(file.label, edited && `Edited ${edited}`)} remember>
                <p className="m-0 break-words text-gray-900">{doc.name}</p>
                <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-500")}>{joinDotted(file.label, doc.owner && `Owner ${doc.owner}`, edited && `Edited ${edited}`)}</p>
                <p className={cn(TYPE.tc, "m-0 mt-2", NAME_TAG_TEXT[tag.tone])}>{tag.text}</p>
                {decided && (!decided.nameMatches || decided.titleMatches === false) && (
                    <div className={cn(SURFACE.inner, "mt-3")}>
                        {!decided.nameMatches && (
                            <>
                                <p className={cn(TYPE.tc, "m-0 text-gray-500")}>In the decided format</p>
                                <p className={cn(TYPE.tc, "m-0 mt-0.5 break-words font-mono text-gray-900")}>{decided.name}</p>
                                {/* Rename (B16): only with the Docs write switch on, and only on a
                                    client Google Doc (canWrite): never a Word file or an internal one.
                                    Where the write views reach this doc, one rename path: the button
                                    opens the Name and title lines view, which carries the before and after. */}
                                {onView && canWrite(doc) ? (
                                    <div className="mt-2">
                                        <PillButton size="sm" icon={PencilLineIcon} onClick={() => onView("lines")}>
                                            Rename to this
                                        </PillButton>
                                    </div>
                                ) : canWrite(doc) ? (
                                    <RenameToHouse
                                        documentId={doc.id}
                                        name={decided.name}
                                        onBusy={busy}
                                        onRenamed={(text) => {
                                            setSaid(null);
                                            voice.note({ tone: "success", text });
                                        }}
                                    />
                                ) : null}
                            </>
                        )}
                        {decided.titleMatches === false && (
                            <>
                                <p className={cn(TYPE.tc, "m-0 text-gray-500", !decided.nameMatches && "mt-2")}>Page title, line 1 of the doc</p>
                                <p className={cn(TYPE.tc, "m-0 mt-0.5 break-words font-mono text-gray-900")}>{decided.title}</p>
                            </>
                        )}
                    </div>
                )}
            </DrawerFold>

            <DrawerFold id="staff-doc.sharing" label="Who can open it" note={shareNote} remember>
                <ul className="m-0 list-plain space-y-2 p-0 text-gray-700">
                    {clients.length > 0 ? (
                        clients.map((p, i) => (
                            <Line key={p.email ?? p.name ?? i} icon={LockIcon} tone="text-[#AF52DE]">
                                {`${p.name ?? p.email ?? "Someone"}${p.email && p.name ? ` (${p.email})` : ""}, ${ROLE_WORD[p.role]}`}
                            </Line>
                        ))
                    ) : (
                        <Line icon={LockIcon}>Not shared with the client&rsquo;s email.</Line>
                    )}
                    <Line icon={UsersIcon} tone="text-[#8E8E93]">
                        {/* The account from the doc's own share list: it differs per environment (#3974). */}
                        {ouraAccount ? `Oura reads it as ${ouraAccount}.` : "Oura reads it with its own Google account."}
                    </Line>
                    <Line icon={HomeIcon} tone="text-[#007AFF]">
                        {shared
                            ? `On the client's Oura page, since it's shared with them${sharedDay ? ` (${sharedDay})` : ""}.`
                            : "Not on the client's Oura page until it's shared to their email."}
                    </Line>
                </ul>
                {inDoc.length === 0 && <DueList lines={due} />}
            </DrawerFold>

            {detail && alsoCount > 0 && (
                <DrawerFold id="staff-doc.also" label="Also in the doc" note={`${alsoCount} not counted`} remember>
                    <AlsoList detail={detail} />
                    {cleanUp.button && <div className="mt-2">{cleanUp.button}</div>}
                </DrawerFold>
            )}

            <DrawerFoot message={footMessage} done={done} confirm={done ? null : confirm} first={done ? undefined : first} second={done ? undefined : second} />
        </>
    );
}
